import { FichaDeReferencia } from "@/components/referencias/FichaDeReferencia";
import { EstadoDeError } from "@/components/estados/EstadoDeError";
import { EstadoSinConexion } from "@/components/estados/EstadoSinConexion";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import type { EstadoDeConsulta } from "@/lib/estadoDeConsulta";
import type { AjusteDeHomologacion } from "@/lib/homologacion";
import type { ReferenciaDelInforme } from "@/lib/referencias";

interface ListaDeReferenciasProps {
  estado: EstadoDeConsulta;
  referencias: ReferenciaDelInforme[];
  /** Qué no se pudo pedir, en palabras del dominio ("las referencias"). */
  que: string;
  tituloDeError: string;
  error: unknown;
  onReintentar: () => void;
  /** Qué decir cuando la consulta salió bien y no trajo nada. */
  vacio: { titulo: string; descripcion: string };
  /** Hay una decisión en vuelo: los botones de TODAS las fichas se apagan. */
  ocupada?: boolean;
  onValidar?: (referencia: ReferenciaDelInforme, validada: boolean) => void;
  onDescartar?: (referencia: ReferenciaDelInforme) => void;
  onRestaurar?: (referencia: ReferenciaDelInforme) => void;
  /**
   * Homologar solo se ofrece donde estas dos props llegan: hoy las dos pestañas
   * de mercado de la ficha del informe, y NO la pantalla de enlazadas del
   * módulo de referencias.
   */
  escalaDeHomologacion?: AjusteDeHomologacion[];
  onAjustar?: (referencia: ReferenciaDelInforme, categoriaIds: number[]) => void;
  /**
   * Corregir los DATOS del comparable. Como `onAjustar`, solo llega donde la
   * acción tiene sentido: hoy las dos pestañas de mercado de la ficha del
   * informe.
   */
  onEditar?: (referencia: ReferenciaDelInforme) => void;
}

/**
 * Un listado de referencias YA ENLAZADAS con sus cuatro estados de consulta.
 *
 * Existe porque lo dibujan DOS pantallas: la de referencias del informe —que
 * las muestra todas juntas, más su papelera— y las dos pestañas de mercado de
 * la ficha del informe, que las parten por clase de evidencia. Lo que comparten
 * no es la lista sino el REPERTORIO DE ESTADOS: sin conexión, error con
 * reintento, esqueletos y vacío. Duplicarlo deja dos pantallas que mienten
 * distinto cuando la red se cae.
 *
 * El vacío y el título del error llegan por prop porque son lo único que cambia
 * de una a otra: "este informe todavía no tiene referencias" y "sin ofertas de
 * mercado" son el mismo estado contando cosas distintas.
 */
export function ListaDeReferencias({
  estado,
  referencias,
  que,
  tituloDeError,
  error,
  onReintentar,
  vacio,
  ocupada = false,
  onValidar,
  onDescartar,
  onRestaurar,
  escalaDeHomologacion,
  onAjustar,
  onEditar,
}: ListaDeReferenciasProps) {
  if (estado.sinConexion) return <EstadoSinConexion que={que} />;

  if (estado.hayError) {
    return (
      <EstadoDeError
        titulo={tituloDeError}
        error={error}
        onReintentar={onReintentar}
        reintentando={estado.recargando}
      />
    );
  }

  if (estado.cargando) {
    return (
      <div className="flex flex-col gap-3">
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} className="h-40 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (referencias.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>{vacio.titulo}</EmptyTitle>
          <EmptyDescription>{vacio.descripcion}</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {referencias.map((referencia) => (
        <FichaDeReferencia
          key={referencia.id}
          referencia={referencia}
          ocupada={ocupada}
          onValidar={onValidar ? (validada) => onValidar(referencia, validada) : undefined}
          onDescartar={onDescartar ? () => onDescartar(referencia) : undefined}
          onRestaurar={onRestaurar ? () => onRestaurar(referencia) : undefined}
          escalaDeHomologacion={escalaDeHomologacion}
          onAjustar={onAjustar ? (ids) => onAjustar(referencia, ids) : undefined}
          onEditar={onEditar ? () => onEditar(referencia) : undefined}
        />
      ))}
    </div>
  );
}
